import { Handle, Position } from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import { Building2, Users, FolderOpen, Plus } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import type { OrgRootNodeData } from '../../hooks/useOrgChartData';

interface OrgRootNodeProps {
  data: OrgRootNodeData;
}

export function OrgRootNode({ data }: OrgRootNodeProps) {
  const { t } = useTranslation('organizationUnits');

  return (
    <div
      className="group relative flex w-50 flex-col gap-1 rounded-xl px-4 py-3 shadow-md"
      style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
        pointerEvents: 'all',
      }}
      data-testid="org-chart-root-node"
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/20">
            <Building2 className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{data.orgName}</p>
            <p className="text-xs text-white/70">{t('orgChart.rootNode.viewLabel')}</p>
          </div>
        </div>

        {data.onCreateArea && (
          <div
            className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="h-6 w-6 text-white/70 hover:bg-white/20 hover:text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    data.onCreateArea?.();
                  }}
                  data-testid="org-chart-root-create-area"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('orgChart.rootNode.createArea')}</TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>

      <div className="mt-1 flex items-center gap-3 border-t border-white/20 pt-2">
        <div className="flex items-center gap-1 text-xs text-white/80">
          <Users className="h-3 w-3" />
          <span>{t('orgChart.rootNode.members', { count: data.memberCount })}</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-white/80">
          <FolderOpen className="h-3 w-3" />
          <span>{t('orgChart.rootNode.projects', { count: data.projectCount })}</span>
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="border-white/40! bg-white/60!" />
    </div>
  );
}
